import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertCircle, ShieldCheck, Zap, BarChart3, Eye, EyeOff, CheckCircle, RefreshCw } from 'lucide-react';
import { authApi } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { getTenantLoginContext } from '@/utils/subdomain';
import { getElectronAPI, isDesktopApp } from '@/desktop/electronApi';
import { useDesktopSessionStore } from '@/desktop/desktopSessionStore';
import { startFreshOnThisPc } from '@/desktop/startFresh';
import { persistDesktopCloudSession } from '@/desktop/cloudSession';

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isTenantLogin, subdomain } = useMemo(() => {
    const ctx = getTenantLoginContext();
    console.log("DEBUG TenantLoginContext:", ctx, "Hostname:", window.location.hostname);
    return ctx;
  }, []);
  const [view, setView] = useState<'login' | 'forgot_password'>('login');
  const [companyCode, setCompanyCode] = useState('');
  const [tenantCompanyName, setTenantCompanyName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [boundCompany, setBoundCompany] = useState<string | null>(null);
  const [setupComplete, setSetupComplete] = useState(false);
  const [freshBusy, setFreshBusy] = useState(false);
  const { setAuth } = useAuthStore();

  useEffect(() => {
    if (!isTenantLogin || !subdomain) return;
    authApi.checkSubdomain(subdomain).then((res) => {
      const company = res.data?.message;
      if (company?.company_name) {
        setTenantCompanyName(company.company_name);
      }
    }).catch(() => {});
  }, [isTenantLogin, subdomain]);

  // Desktop: show where company code came from (Electron config + last login localStorage).
  useEffect(() => {
    if (!isDesktopApp()) return;
    void (async () => {
      const api = getElectronAPI();
      const cfg = api ? await api.getConfig() : null;
      const fromConfig = cfg?.boundCompanyCode || null;
      const fromLs = localStorage.getItem('company_code');
      const bound = fromConfig || useDesktopSessionStore.getState().boundCompanyCode || null;
      setBoundCompany(bound);
      setSetupComplete(Boolean(cfg?.setupComplete || useDesktopSessionStore.getState().setupComplete));
      // Prefill only as hint — user can change; early bind no longer locks until wizard finishes.
      if (!companyCode && (bound || fromLs)) {
        setCompanyCode(bound || fromLs || '');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStartFresh = async () => {
    if (
      !window.confirm(
        'Start fresh on this PC?\n\nThis clears the saved company bind (e.g. 2010), PIN unlocks, and local setup flags. You will log in again and run the setup wizard.',
      )
    ) {
      return;
    }
    setFreshBusy(true);
    setError('');
    try {
      await startFreshOnThisPc();
      setBoundCompany(null);
      setSetupComplete(false);
      setCompanyCode('');
      setUsername('');
      setPassword('');
      setSuccessMessage('This PC was reset. Log in with your company code to configure again.');
    } catch (e) {
      setError((e as Error)?.message || 'Could not reset this PC');
    } finally {
      setFreshBusy(false);
    }
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      const res = await authApi.login({
        username,
        password,
        ...(isTenantLogin && subdomain
          ? { subdomain }
          : { company_code: companyCode }),
      });
      const data = res.data.message;
      // document.cookie on file:// does not set the Frappe API-host sid — use Electron bridge.
      if (isDesktopApp() && data.sid) {
        await persistDesktopCloudSession({
          sid: data.sid,
          userId: data.user?.name,
          fullName: data.user?.full_name,
        });
      } else if (data.sid) {
        document.cookie = `sid=${data.sid}; path=/; SameSite=Lax`;
      }
      setAuth(data.user, data.company, data.role, data.can_use_pos);
      // Desktop: bind company only after setup is already complete; first run goes to wizard without locking yet.
      if (isDesktopApp()) {
        const api = getElectronAPI();
        const code = data.company?.code || '';
        const cfg = api ? await api.getConfig() : null;
        const bound = cfg?.boundCompanyCode || useDesktopSessionStore.getState().boundCompanyCode;
        const setupDone =
          Boolean(cfg?.setupComplete) || useDesktopSessionStore.getState().setupComplete;

        if (bound && code && bound !== code && setupDone) {
          setError(
            `This PC is bound to company "${bound}". Use “Start fresh on this PC” below to unbind, then log in with another company.`,
          );
          useAuthStore.getState().clearAuth();
          setIsSubmitting(false);
          return;
        }

        useDesktopSessionStore.getState().setCloudSessionExpired(false);

        const params = new URLSearchParams(window.location.search);
        const resetPin =
          params.get('resetPin') === '1' || useDesktopSessionStore.getState().resetPinAfterLogin;

        if (resetPin && setupDone) {
          useDesktopSessionStore.getState().setResetPinAfterLogin(true);
          useDesktopSessionStore.getState().setPendingEnrollAfterLogin(true);
          useDesktopSessionStore.setState({ locallyUnlocked: false, locked: false });
          const isAdmin = data.role === 'Company Admin' || data.role === 'System Administrator';
          navigate(isAdmin ? '/dashboard' : '/', { replace: true });
          return;
        }

        if (!setupDone) {
          // Do NOT write boundCompanyCode until wizard finishes (avoids sticky company on failed setup).
          useDesktopSessionStore.getState().setSetupState({
            pendingDesktopSetup: true,
            boundCompanyCode: null,
            setupComplete: false,
          });
          useDesktopSessionStore.setState({ locallyUnlocked: false, locked: false });
          navigate('/desktop-setup', { replace: true });
          return;
        }

        // Already set up — enroll PIN if missing for this user
        if (api && code && !bound) {
          await api.setConfig({ boundCompanyCode: code });
          useDesktopSessionStore.getState().setSetupState({ boundCompanyCode: code });
        }
        useDesktopSessionStore.getState().setPendingEnrollAfterLogin(true);
        useDesktopSessionStore.setState({ locallyUnlocked: true, locked: false });
      }
      const isAdmin = data.role === 'Company Admin' || data.role === 'System Administrator';
      navigate(isAdmin ? '/dashboard' : '/', { replace: true });
    } catch (err: any) {
      let msg = err?.response?.data?.message;
      if (err?.response?.data?._server_messages) {
        try {
          const messages = JSON.parse(err.response.data._server_messages);
          if (messages && messages.length > 0) {
            msg = JSON.parse(messages[0]).message;
          }
        } catch (e) {
          // ignore
        }
      }
      if (msg === 'No App') msg = 'Invalid Company Code or Subdomain.';
      setError(msg || 'Login failed. Please check your credentials.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setIsSubmitting(true);
    try {
      const frontendUrl = window.location.origin;
      await authApi.forgotPassword({
        username,
        frontend_url: frontendUrl,
        ...(isTenantLogin && subdomain
          ? { subdomain }
          : { company_code: companyCode }),
      });
      setSuccessMessage('A password reset link has been sent to your email.');
    } catch (err: any) {
      let msg = err?.response?.data?.message;
      if (err?.response?.data?._server_messages) {
        try {
          const messages = JSON.parse(err.response.data._server_messages);
          if (messages && messages.length > 0) {
            msg = JSON.parse(messages[0]).message;
          }
        } catch (e) {
          // ignore
        }
      }
      if (msg === 'No App') msg = 'Invalid Company Code or Subdomain.';
      setError(msg || 'Failed to send reset link. Please check your details.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const features = [
    { icon: Zap, title: 'Fast Billing', desc: 'Create invoices in seconds' },
    { icon: BarChart3, title: 'Live Reports', desc: 'Real-time sales analytics' },
    { icon: ShieldCheck, title: 'Secure & Reliable', desc: 'Your data, always safe' },
  ];

  return (
    <div className="min-h-screen flex">
      {/* ── Left panel ── */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-[#0A2A73] via-[#0d3494] to-[#0a1e4f] flex-col items-center justify-center p-12">
        {/* Ambient glows */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#1FA8FF]/20 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#1FA8FF]/10 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(31,168,255,0.08)_0%,_transparent_70%)]" />

        {/* Grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,1) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,1) 1px,transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />

        <div className="relative z-10 max-w-sm w-full">
          {/* Logo on white pill — keeps dark SVG visible */}
          <div className="bg-white rounded-2xl px-8 py-5 inline-block mb-10 shadow-xl shadow-black/30">
            <img src="/saledesk-logo.svg" alt="Sale Desk" className="h-12 w-auto" />
          </div>

          <h2 className="text-3xl font-bold text-white mb-3 leading-tight">
            Smart POS for<br />Modern Businesses
          </h2>
          <p className="text-blue-200 text-sm mb-10 leading-relaxed">
            Manage sales, inventory, and reports — all in one place built for CCTV, Networking &amp; IT businesses.
          </p>

          <div className="space-y-5">
            {features.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#1FA8FF]/20 border border-[#1FA8FF]/30 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-[#1FA8FF]" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">{title}</p>
                  <p className="text-blue-300 text-xs">{desc}</p>
                </div>
              </div>
            ))}
          </div>

          <p className="mt-12 text-blue-400 text-xs">
            © {new Date().getFullYear()} Sale Desk. All rights reserved.
          </p>
        </div>
      </div>

      {/* ── Right panel (form) ── */}
      <div className="flex-1 flex flex-col items-center justify-center bg-gray-50 p-6">
        <div className="w-full max-w-md">

          {/* Mobile-only logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <div className="">
              <img src="/saledesk-logo.svg" alt="SaleDesk POS" className="h-14 w-auto" />
            </div>
          </div>

          {/* Card */}
          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8">
            <div className="mb-7">
              <h1 className="text-2xl font-bold text-gray-900">
                {view === 'login' ? t('auth.welcomeBack') : t('users.resetPassword')}
              </h1>
              <p className="text-gray-500 text-sm mt-1">
                {view === 'login'
                  ? isTenantLogin && subdomain
                    ? `Sign in to ${tenantCompanyName || subdomain}.saledesk.pk`
                    : t('auth.signInSubtitle')
                  : 'Enter your details to receive a reset link'}
              </p>
            </div>

            {error && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm mb-5">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            
            {successMessage && (
              <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-xl text-sm mb-5">
                <CheckCircle className="w-4 h-4 flex-shrink-0" />
                <span>{successMessage}</span>
              </div>
            )}

            {isDesktopApp() && (boundCompany || setupComplete) && view === 'login' && (
              <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 space-y-2">
                <p>
                  This PC still remembers company{' '}
                  <strong>{boundCompany || companyCode || '—'}</strong>
                  {setupComplete ? ' (setup already finished)' : ''}.
                </p>
                <p className="text-xs text-amber-800/80">
                  Stored in Electron AppData <code className="text-[10px]">config.json</code> (
                  <code className="text-[10px]">boundCompanyCode</code>) plus last login in browser
                  storage — not from the installer download.
                </p>
                <button
                  type="button"
                  disabled={freshBusy}
                  onClick={() => void handleStartFresh()}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-950 underline disabled:opacity-50"
                >
                  {freshBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  Start fresh on this PC
                </button>
              </div>
            )}

            {view === 'login' ? (
              <form onSubmit={handleLoginSubmit} className="space-y-5">
                {!isTenantLogin && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('auth.companyCode')}</label>
                    <input
                      required
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1FA8FF]/50 focus:border-[#1FA8FF] focus:bg-white transition-all"
                      value={companyCode}
                      onChange={(e) => setCompanyCode(e.target.value)}
                      placeholder="e.g., 2001"
                    />
                    <p className="text-xs text-gray-400 mt-1">Found in your welcome email</p>
                  </div>
                )}

                {/* Username */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('auth.username')}</label>
                  <input
                    required
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1FA8FF]/50 focus:border-[#1FA8FF] focus:bg-white transition-all"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter username"
                  />
                </div>

                {/* Password */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-sm font-medium text-gray-700">{t('auth.password')}</label>
                    <button type="button" onClick={() => { setView('forgot_password'); setError(''); setSuccessMessage(''); }} className="text-xs font-medium text-[#1FA8FF] hover:underline focus:outline-none">{t('auth.forgotPassword')}</button>
                  </div>
                  <div className="relative">
                    <input
                      required
                      type={showPassword ? "text" : "password"}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1FA8FF]/50 focus:border-[#1FA8FF] focus:bg-white transition-all pr-12"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3.5 rounded-xl font-semibold text-white text-sm flex items-center justify-center gap-2 mt-1 transition-all disabled:opacity-60"
                  style={{
                    background: isSubmitting
                      ? '#6b7280'
                      : 'linear-gradient(135deg,#1FA8FF 0%,#0A2A73 100%)',
                    boxShadow: '0 4px 20px rgba(31,168,255,0.35)',
                  }}
                >
                  {isSubmitting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> {t('auth.signingIn')}</>
                  ) : (
                    t('auth.signIn')
                  )}
                </button>
              </form>
            ) : (
              <form onSubmit={handleForgotSubmit} className="space-y-5">
                {!isTenantLogin && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('auth.companyCode')}</label>
                    <input
                      required
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1FA8FF]/50 focus:border-[#1FA8FF] focus:bg-white transition-all"
                      value={companyCode}
                      onChange={(e) => setCompanyCode(e.target.value)}
                      placeholder="e.g., 2001"
                    />
                  </div>
                )}

                {/* Username */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('auth.username')}</label>
                  <input
                    required
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1FA8FF]/50 focus:border-[#1FA8FF] focus:bg-white transition-all"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter username"
                  />
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting || !!successMessage}
                  className="w-full py-3.5 rounded-xl font-semibold text-white text-sm flex items-center justify-center gap-2 mt-1 transition-all disabled:opacity-60"
                  style={{
                    background: (isSubmitting || !!successMessage)
                      ? '#6b7280'
                      : 'linear-gradient(135deg,#1FA8FF 0%,#0A2A73 100%)',
                    boxShadow: '0 4px 20px rgba(31,168,255,0.35)',
                  }}
                >
                  {isSubmitting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Sending Link…</>
                  ) : (
                    'Send Reset Link'
                  )}
                </button>

                <div className="text-center mt-4">
                  <button type="button" onClick={() => { setView('login'); setError(''); setSuccessMessage(''); }} className="text-sm font-medium text-gray-500 hover:text-gray-700 focus:outline-none">
                    ← Back to Login
                  </button>
                </div>
              </form>
            )}
          </div>

          <p className="text-center text-sm text-gray-400 mt-6">
            Don&apos;t have an account?{' '}
            <a href="/signup" className="text-[#1FA8FF] font-medium hover:underline transition-colors">
              {t('auth.startTrial')}
            </a>
          </p>
          <p className="text-center text-xs text-gray-400 mt-3">
            By continuing you agree to our processing of account data to operate this POS.
            Company admins can export or anonymize customer data from Settings → Privacy &amp; data.
          </p>
        </div>
      </div>
    </div>
  );
}
